import React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { I18n } from "@lingui/react";
import { Trans } from "@lingui/macro";
import {
  DataGrid,
  DataGridFooter,
  Button,
  MainText,
  ControlGroup,
  Left,
  Center,
  Right,
} from "@conserveit/blocks";
import { updateSortFunc } from "../../../../redux/modules/_BETA/actionsUI";
import NoResultsOverlay from "../../../NoResultsOverlay";

import IssuePane from "../IssuePane";
import ListHeader from "../ListHeader";
import ListRow from "../ListRow";

import { Container, Content, StyledThirds } from "./ListView.styles";

const DEFAULT_NUM_ROWS: number = 15;

interface BetaIssues extends Insite.AppState {
  betaIssues: {
    loading: boolean;
  };
}
export interface Row {
  context: string;
  key: string;
  id: string;
  summary: string;
  priority: string;
  bonfire_status: string;
  created: any;
  updated: any;
  assignee_display: string;
  responsible: string;
  components: string;
  project_name: string;
  project_key: string;
}

interface OwnProps {
  sidebarOpen: boolean;
  onHideSidebar: () => void;
  onShowSidebar: () => void;
  onRowClick(id: string): () => void;
  actionId: string;
  rows: Array<Row>;
  noResults: boolean;
}
interface ConnectedProps {
  actionListLoading: boolean;
  activeAction: Insite.AppState["betaAction"]["action"];
  actionLoading: Insite.AppState["betaAction"]["isLoading"];
  activeColumns: Insite.AppState["betaActionsUI"]["activeColumns"];
  sortFilter: Insite.AppState["betaActionsUI"]["sortFilter"];
  sortDirection: Insite.AppState["betaActionsUI"]["sortDirection"];
  comments: Insite.AppState["betaAction"]["comments"];
  user: Insite.AppState["auth"]["user"];
  userAccess: Insite.AppState["auth"]["user"]["access"];
}

interface DispatchProps {
  updateSort: (key: string) => void;
}
type Props = OwnProps & ConnectedProps & DispatchProps;

interface State {
  numRowsVisible: typeof DEFAULT_NUM_ROWS;
}

function mapStateToProps(state: BetaIssues): ConnectedProps {
  return {
    actionListLoading: state.betaIssues.loading,
    // IssuePane
    activeAction: state.betaAction.action,
    actionLoading: state.betaAction.isLoading,
    activeColumns: state.betaActionsUI.activeColumns,
    sortFilter: state.betaActionsUI.sortFilter,
    sortDirection: state.betaActionsUI.sortDirection,
    comments: state.betaAction.comments,
    user: state.auth.user,
    userAccess: state.auth.user.access,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    updateSort: (key: string) => dispatch(updateSortFunc(key)),
  };
}

class ListView extends React.Component<Props, State> {
  state = {
    numRowsVisible: DEFAULT_NUM_ROWS,
  };

  handleShowMore = () => {
    this.setState((state) => ({
      numRowsVisible: state.numRowsVisible + 10,
    }));
  };

  handleShowAll = () => {
    this.setState({
      numRowsVisible: this.props.rows.length + 1,
    });
  };

  handleClickHeader = (key: string) => () => {
    this.props.updateSort(key);
  };

  handleHiddenColumns = (
    activeColumns: Insite.AppState["betaActionsUI"]["activeColumns"]
  ) => {
    // if side bar is open, then automatically hide the last four columns
    const hiddenColumns: number[] = this.props.sidebarOpen ? [5, 6, 7, 8] : [];
    for (const key in activeColumns) {
      const index = Object.keys(activeColumns).indexOf(key) + 1;
      if (!(activeColumns as Record<string, boolean>)[key]) {
        hiddenColumns.push(index);
      }
    }

    return hiddenColumns;
  };
  render() {
    const visible = this.props.rows.slice(0, this.state.numRowsVisible);
    const {
      activeAction = {},
      activeColumns,
      sortFilter,
      sortDirection,
      actionLoading = true,
    } = this.props;
    const hiddenColumns = this.handleHiddenColumns(activeColumns);
    return (
      <Container sidebarOpen={this.props.sidebarOpen}>
        <Content>
          {this.props.noResults && <NoResultsOverlay noResultsMessage />}
          <I18n>
            {({ i18n }) => (
              <React.Fragment>
                <DataGrid
                  group={[{ start: 1, end: 8 }]}
                  hide={hiddenColumns}
                  cellProps={{ type: "dense", density: "default" }}
                  gridTemplateColumns={["80px", "120px", "200px"]}
                  columns={hiddenColumns.length === 0 ? 8 : 4}
                >
                  <ListHeader
                    sortFilter={sortFilter}
                    sortDirection={sortDirection}
                    i18n={i18n}
                    handleClickHeader={this.handleClickHeader}
                  />

                  {visible.map((row) => (
                    <ListRow
                      onClick={this.props.onRowClick(row.key)}
                      isActive={activeAction.key === row.key}
                      isDisabled={actionLoading}
                      context={row.context}
                      key={row.key}
                      id={row.key}
                      summary={row.summary}
                      priority={row.priority}
                      status={row.bonfire_status}
                      createdAt={row.created}
                      updatedAt={row.updated}
                      assignee={row.assignee_display}
                      responsible={row.responsible}
                      siteName={row.components}
                      projectName={row.project_name}
                      projectKey={row.project_key}
                    />
                  ))}
                </DataGrid>
                <DataGridFooter>
                  <StyledThirds>
                    <Left>
                      <MainText>
                        <Trans id="show_more_actions_footer_row.showing_count">
                          Showing <strong>{visible.length}</strong> of{" "}
                          <strong>{this.props.rows.length}</strong>
                        </Trans>
                      </MainText>
                    </Left>
                    <Center>
                      <ControlGroup tight>
                        {this.props.rows.length > visible.length && (
                          <Button
                            primary
                            thin
                            appearance="outline"
                            onClick={this.handleShowMore}
                          >
                            <Trans id="show_more_actions_footer_row.show_more">
                              Show More
                            </Trans>
                          </Button>
                        )}
                        {this.props.rows.length > visible.length && (
                          <Button
                            primary
                            thin
                            appearance="plain"
                            onClick={this.handleShowAll}
                          >
                            <Trans id="show_more_actions_footer_row.show_all">
                              Show all
                            </Trans>
                          </Button>
                        )}
                      </ControlGroup>
                    </Center>
                    <Right />
                  </StyledThirds>
                </DataGridFooter>
              </React.Fragment>
            )}
          </I18n>
        </Content>

        {activeAction.key !== undefined && (
          <IssuePane
            equipRules={activeAction.associatedEquips}
            userAccess={this.props.userAccess}
            comments={this.props.comments}
            issue={activeAction}
            isLoading={this.props.actionListLoading}
            onClose={this.props.onHideSidebar}
          />
        )}
      </Container>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ListView);
